import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Star, ThumbsUp, Edit, Trash, Filter, Calendar, User, Shield,
  BarChart2, TrendingUp, Award, MessageCircle, ChevronDown,
  ThumbsDown, Share2, Flag, Download, CheckCircle, Loader2,
  ChevronLeft, ChevronRight, MessageSquare, Send
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "./Button";
import { Badge } from "../../components/ui/badge";
import SharedFooter3 from "../../Footer/SharedFooter3";
import SharedHeader4 from "../../Headers/SharedHeader4";
import SharedHeader5 from "../../Headers/SharedHeader5";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { fetchReviewsByAuthor, updateReview } from "../../redux/Slices/reviewSlice";
import { Review as ReviewType, UserRef, RoleEnum } from "../../modules/shared/model";
import { GlobalService } from "../../modules";
import { useNavigate } from "react-router-dom";
import VoteDisplayButton from "./VoteDisplayButton";
import ReplyVoteButton from "./ReplyVoteButton";

interface ReviewDisplay {
  id: string;
  author: string;
  authorName?: string;
  date: string;
  rating: number;
  text: string;
  project: string;
  clientCompany?: string;
  verified: boolean;
  helpful: number;
  notHelpful: number;
  response?: string;
  category: string;
  tags: string[];
  engagement: {
    views: number;
    shares: number;
    comments: number;
  };
  // Additional fields from API
  qualityOfService?: number;
  professionalism?: number;
  punctuality?: number;
  communication?: number;
  qualityPriceRatio?: number;
  overallExperience?: number;
  clarity?: number;
  reliability?: number;
  payment?: number;
  availability?: number;
  canReply?: boolean;
  user: UserRef;
  authorPhoto?: string;
  review?: ReviewType[] | null;
}

// Helper function to transform API review to display format
const transformReviewForDisplay = (review: ReviewType): ReviewDisplay => {
  const authorName = review.author?.firstName && review.author?.lastName
    ? `${review.author.firstName} ${review.author.lastName}`.trim()
    : review.author?.id || "Unknown User";
  const userBeingReviewed = review.user?.firstName && review.user?.lastName
    ? `${review.user.firstName} ${review.user.lastName}`.trim()
    : review.user?.id || "Unknown User";

  return {
    id: review.id,
    author: userBeingReviewed, // Show the user being reviewed as the main display
    authorName: userBeingReviewed, // Show the user being reviewed as the main display
    date: new Date(review.createdAt).toLocaleDateString(),
    rating: review.rating || 0,
    text: review.text || "No comment provided",
    project: review.milestone?.id ? `Milestone ${review.milestone.id}` : "Project Work",
    clientCompany: "Client",
    verified: true, // Assume verified for now
    helpful: review.helpful || 0,
    notHelpful: review.notHelpful || 0,
    response: review.reply || undefined, // Use reply field from API
    category: "Service", // Default category
    tags: review.tags || [],
    user: review.user,
    authorPhoto: review.user?.photo?.path, // Show photo of the user being reviewed
    engagement: {
      views: review.views || 0,
      shares: review.shares || 0,
      comments: 0 // No comments field in current API
    },
    // Additional rating criteria
    qualityOfService: review.qualityOfService,
    professionalism: review.professionalism,
    punctuality: review.punctuality,
    communication: review.communication,
    qualityPriceRatio: review.qualityPriceRatio,
    overallExperience: review.overallExperience,
    clarity: review.clarity,
    reliability: review.reliability,
    payment: review.payment,
    availability: review.availability,
    canReply: review.canReply || false,
    review: review.review || null
  };
};

// Component for displaying star ratings
const StarRatingDisplay: React.FC<{ rating: number; size?: "sm" | "md" | "lg" }> = ({ rating, size = "sm" }) => {
  const sizeClasses = {
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-5 w-5"
  };

  return (
    <div className="flex items-center">
      {[1, 2, 3, 4, 5].map((star) => {
        const isHalfFilled = rating >= star - 0.5 && rating < star;
        const isFilled = rating >= star;

        return (
          <div key={star} className="relative">
            <Star
              className={`${sizeClasses[size]} ${isFilled
                  ? "fill-yellow-400 text-yellow-400"
                  : "text-gray-300"
                }`}
            />
            {isHalfFilled && (
              <div className="absolute left-0 top-0 w-1/2 h-full overflow-hidden">
                <Star
                  className={`${sizeClasses[size]} fill-yellow-400 text-yellow-400`}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, trend, color }) => (
  <Card className="p-6 h-full">
    <div className="flex items-start justify-between">
      <div className={`rounded-full p-3 ${color}`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
      {trend && (
        <Badge variant={undefined} className={trend > 0 ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}>
          {trend > 0 ? "+" : ""}{trend}%
        </Badge>
      )}
    </div>
    <div className="mt-4">
      <h3 className="text-2xl font-bold">{value}</h3>
      <p className="text-gray-600 text-sm mt-1">{label}</p>
    </div>
  </Card>
);

const ProfessionalReviewsSection = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const {
    authorReviews,
    authorReviewsLoading,
    authorReviewsError,
    authorReviewsTotalCount,
    authorReviewsHasNextPage
  } = useAppSelector((state) => state.reviews);
  const navigate = useNavigate();

  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showAnalytics, setShowAnalytics] = useState(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [reviewsPerPage] = useState<number>(10);
  const [expandedReviews, setExpandedReviews] = useState<Set<string>>(new Set());
  
  // Reply state
  const [replyingToReview, setReplyingToReview] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [expandedNestedReviews, setExpandedNestedReviews] = useState<Set<string>>(new Set());

  // Handle vote submission
  const handleVote = async (reviewId: string, voteType: "helpful" | "unhelpful") => {
    try {
      // You would typically call an API here to submit the vote
      console.log(`Voting ${voteType} for review ${reviewId}`);
      // Example: await dispatch(updateReviewVote({ reviewId, voteType }));
    } catch (error) {
      console.error("Error submitting vote:", error);
    }
  };


  // Fetch reviews on component mount
  useEffect(() => {
    if (user?.id) {
      // Only fetch reviews given by this user (as author)
      dispatch(fetchReviewsByAuthor({
        authorId: user.id,
        query: { page: currentPage, limit: reviewsPerPage, sort: [{ orderBy: "createdAt", order: "DESC" }] }
      }));
    }
  }, [dispatch, user?.id, currentPage, reviewsPerPage]);

  // Calculate stats from real data
  const professionalStats = useMemo(() => {
    const reviewsGiven = authorReviews.length;
    const averageRatingGiven = reviewsGiven > 0
      ? (authorReviews.reduce((sum, review) => sum + (review.rating || 0), 0) / reviewsGiven).toFixed(1)
      : "0.0";

    return [
      {
        icon: Star,
        label: t("professionalReviewsSection.averageRatingGiven"),
        value: `${averageRatingGiven}/5`,
        trend: 0, // Could calculate trend if we had historical data
        color: "bg-blue-600"
      },
      {
        icon: CheckCircle,
        label: t("professionalReviewsSection.totalReviewsGiven"),
        value: reviewsGiven.toString(),
        trend: 0,
        color: "bg-green-600"
      },
      {
        icon: MessageCircle,
        label: t("professionalReviewsSection.clientSatisfaction"),
        value: `${averageRatingGiven}/5`,
        trend: 0,
        color: "bg-purple-600"
      },
      {
        icon: Award,
        label: t("professionalReviewsSection.professionalRating"),
        value: `${averageRatingGiven}/5`,
        trend: 0,
        color: "bg-yellow-600"
      }
    ];
  }, [authorReviews]);

  // Check if client can reply to a review
  const canClientReply = (displayReview: ReviewDisplay) => {
    if (!user || Number(user.role?.id) !== RoleEnum.client) return false;
    
    // Use the canReply field from the review object
    return displayReview.canReply === true;
  };

  // Handle reply submission
  const handleReplySubmit = async (reviewId: string) => {
    if (!replyText.trim() || isSubmittingReply) return;
    
    setIsSubmittingReply(true);
    try {
      await dispatch(updateReview({ id: reviewId, data: { reply: replyText.trim() } }));
      
      // Refresh reviews to get updated data
      if (user?.id) {
        dispatch(fetchReviewsByAuthor({ authorId: user.id, query: { page: currentPage, limit: reviewsPerPage } }));
      }
      
      setReplyingToReview(null);
      setReplyText("");
    } catch (error) {
      console.error("Failed to submit reply:", error);
    } finally {
      setIsSubmittingReply(false);
    }
  };

  // Handle reply cancel
  const handleReplyCancel = () => {
    setReplyingToReview(null);
    setReplyText("");
  };

  // Get reviews based on filter - only show reviews given by this professional
  const getReviewsForFilter = () => {
    return authorReviews; // Only show reviews given by this professional
  };

  const filteredAndSortedReviews = useMemo(() => {
    const reviews = getReviewsForFilter();
    const transformedReviews = reviews.map(transformReviewForDisplay);

    const filtered = transformedReviews.filter(review => {
      if (selectedCategory !== "all" && review.category !== selectedCategory) return false;
      return true;
    });

    // Sorting logic based on filterStatus and sortBy
    if (filterStatus === "rating" || sortBy === "rating") {
      filtered.sort((a, b) => b.rating - a.rating); // Highest rating first
    } else if (filterStatus === "recent" || sortBy === "recent") {
      filtered.sort((a, b) => {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        return dateB - dateA; // Most recent first
      });
    }

    return filtered;
  }, [filterStatus, selectedCategory, sortBy, authorReviews]);

  // Handle page change
  const handlePageChange = (page: number) => {
    if (page >= 1) {
      setCurrentPage(page);
    }
  };

  // Handle review text expansion
  const toggleReviewExpansion = (reviewId: string) => {
    setExpandedReviews(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reviewId)) {
        newSet.delete(reviewId);
      } else {
        newSet.add(reviewId);
      }
      return newSet;
    });
  };

  // Toggle nested reviews expansion
  const toggleNestedReviews = (reviewId: string) => {
    setExpandedNestedReviews(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reviewId)) {
        newSet.delete(reviewId);
      } else {
        newSet.add(reviewId);
      }
      return newSet;
    });
  };

  // Calculate pagination with fallback for hasNextPage
  const totalPages = Math.ceil((authorReviewsTotalCount || 0) / reviewsPerPage);
  const shouldShowPagination = authorReviewsTotalCount > reviewsPerPage;
  const hasPreviousPage = currentPage > 1;
  const hasNextPage = authorReviewsHasNextPage ?? (currentPage * reviewsPerPage < (authorReviewsTotalCount || 0));

  // Debug logging
  console.log("ProfessionalReviewsSection Debug:", {
    authorReviewsTotalCount,
    authorReviewsHasNextPage,
    hasNextPage,
    currentPage,
    reviewsPerPage,
    totalPages,
    shouldShowPagination
  });

  // Handle loading and error states
  const isLoading = authorReviewsLoading;
  const error = authorReviewsError;

  return (
    <div className="flex flex-col min-h-screen bg-gray-50" style={{ textAlign: "left" }}>
      <SharedHeader4 />

      <div className="flex-grow px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto w-full">
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{t("professionalReviewsSection.title")}</h2>
              <p className="text-gray-600 mt-1">{t("professionalReviewsSection.subtitle")}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              {/* <Button variant="outline" className="flex items-center gap-2 flex-1 justify-center">
                <Download className="h-4 w-4" />
                Export Reviews
              </Button>
              <Button className="flex items-center gap-2 flex-1 justify-center">
                <MessageCircle className="h-4 w-4" />
                Contact Support
              </Button> */}
            </div>
          </div>

          {showAnalytics && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {professionalStats.map((stat, index) => (
                <StatCard key={index} {...stat} />
              ))}
            </div>
          )}

          <Card className="overflow-hidden shadow-lg">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 sm:p-6 border-b">
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                <div className="flex flex-col sm:flex-row flex-wrap gap-4 w-full lg:w-auto">
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-4 py-2 border rounded-lg bg-white shadow-sm w-full sm:w-auto"
                  >
                    <option value="all">{t("professionalReviewsSection.allReviewsGiven")}</option>
                    <option value="recent">{t("professionalReviewsSection.mostRecent")}</option>
                    <option value="rating">{t("professionalReviewsSection.highestRating")}</option>
                  </select>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-4 py-2 border rounded-lg bg-white shadow-sm w-full sm:w-auto"
                  >
                    <option value="recent">{t("professionalReviewsSection.mostRecent")}</option>
                    <option value="rating">{t("professionalReviewsSection.highestRating")}</option>
                    <option value="engagement">{t("professionalReviewsSection.mostEngaged")}</option>
                  </select>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-4 py-2 border rounded-lg bg-white shadow-sm w-full sm:w-auto"
                  >
                    <option value="all">{t("professionalReviewsSection.allCategories")}</option>
                    <option value="Development">{t("professionalReviewsSection.development")}</option>
                    <option value="Design">{t("professionalReviewsSection.design")}</option>
                    <option value="Marketing">{t("professionalReviewsSection.marketing")}</option>
                  </select>
                </div>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2 w-full lg:w-auto justify-center"
                  onClick={() => setShowAnalytics(!showAnalytics)}
                >
                  <BarChart2 className="h-4 w-4" />
                  {showAnalytics ? t("professionalReviewsSection.hideAnalytics") : t("professionalReviewsSection.showAnalytics")}
                </Button>
              </div>
            </div>

            <div className="divide-y">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  <span className="ml-3 text-gray-600">{t("professionalReviewsSection.loading")}</span>
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <div className="text-red-500 mb-4">
                    <Shield className="h-12 w-12 mx-auto mb-2" />
                    <p className="text-lg font-medium">{t("professionalReviewsSection.errorLoadingReviews")}</p>
                    <p className="text-sm text-gray-600">{error}</p>
                  </div>
                  <Button
                    onClick={() => {
                      if (user?.id) {
                        dispatch(fetchReviewsByAuthor({
                          authorId: user.id,
                          query: { page: currentPage, limit: reviewsPerPage,sort: [{ orderBy: "createdAt", order: "DESC" }] }
                        }));
                      }
                    }}
                    variant="outline"
                  >
                    {t("professionalReviewsSection.tryAgain")}
                  </Button>
                </div>
              ) : filteredAndSortedReviews.length === 0 ? (
                <div className="text-center py-12">
                  <Star className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-lg font-medium text-gray-900">{t("professionalReviewsSection.noReviewsFound")}</p>
                  <p className="text-gray-600 mt-1">
                    {t("professionalReviewsSection.noReviewsDesc")}
                  </p>
                </div>
              ) : (
                <AnimatePresence>
                  {filteredAndSortedReviews.map((review) => {
                    // Find the original review data to access votes
                    const originalReview = authorReviews.find(r => r.id === review.id) || {};
                    
                    return (
                    <motion.div
                      key={review.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                      className="p-4 sm:p-6 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white flex-shrink-0 overflow-hidden">
                            {review.authorPhoto ? (
                              <img
                                src={review.authorPhoto}
                                alt={review.authorName || "User"}
                                className="w-full h-full object-cover rounded-full"
                              />
                            ) : (
                              <User className="h-6 w-6" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="font-semibold text-lg">{review.authorName || "Anonymous"}</h3>
                              {review.verified && (
                                <Badge variant="secondary" className="bg-green-50 text-green-700">
                                  <Shield className="h-3 w-3 mr-1" />
                                  {t("professionalReviewsSection.verified")}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 font-medium">{review.clientCompany}</p>
                            <div className="flex flex-wrap items-center gap-4 mt-1">
                              <span className="text-sm text-gray-500 flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                {review.date}
                              </span>
                              <span className="text-sm text-gray-500 flex items-center gap-1">
                                <MessageCircle className="h-4 w-4" />
                                {review.engagement.comments} {t("professionalReviewsSection.comments")}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-start md:items-end gap-2">
                          <div className="flex items-center">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`h-5 w-5 ${star <= review.rating
                                  ? "fill-yellow-400 text-yellow-400"
                                  : "text-gray-300"
                                  }`}
                              />
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <Badge variant={undefined} className="bg-blue-50 text-blue-700">
                              {review.category}
                            </Badge>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4">
                        <p className="text-gray-700 leading-relaxed">
                          {review.text && review.text.length > 200 ? (
                            <span>
                              {expandedReviews.has(review.id) ? review.text : `${review.text.substring(0, 200)}...`}
                              <button
                                onClick={() => toggleReviewExpansion(review.id)}
                                className="ml-2 text-blue-600 hover:text-blue-800 font-medium"
                              >
                                {expandedReviews.has(review.id) ? t("professionalReviewsSection.showLess") : t("professionalReviewsSection.seeMore")}
                              </button>
                            </span>
                          ) : (
                            review.text
                          )}
                        </p>

                        {/* Show detailed ratings if available */}
                        {(review.qualityOfService || review.professionalism || review.punctuality ||
                          review.communication || review.qualityPriceRatio || review.overallExperience ||
                          review.clarity || review.reliability || review.payment || review.availability) && (
                            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                              <h4 className="text-sm font-medium text-gray-900 mb-3">{t("professionalReviewsSection.detailedRatings")}</h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                {review.qualityOfService && (
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-700">{t("professionalReviewsSection.qualityOfService")}</span>
                                    <div className="flex items-center gap-2">
                                      <StarRatingDisplay rating={review.qualityOfService} size="sm" />
                                      <span className="text-xs text-gray-500">{review.qualityOfService}/5</span>
                                    </div>
                                  </div>
                                )}
                                {review.professionalism && (
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-700">{t("professionalReviewsSection.professionalism")}</span>
                                    <div className="flex items-center gap-2">
                                      <StarRatingDisplay rating={review.professionalism} size="sm" />
                                      <span className="text-xs text-gray-500">{review.professionalism}/5</span>
                                    </div>
                                  </div>
                                )}
                                {review.punctuality && (
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-700">{t("professionalReviewsSection.punctuality")}</span>
                                    <div className="flex items-center gap-2">
                                      <StarRatingDisplay rating={review.punctuality} size="sm" />
                                      <span className="text-xs text-gray-500">{review.punctuality}/5</span>
                                    </div>
                                  </div>
                                )}
                                {review.communication && (
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-700">{t("professionalReviewsSection.communication")}</span>
                                    <div className="flex items-center gap-2">
                                      <StarRatingDisplay rating={review.communication} size="sm" />
                                      <span className="text-xs text-gray-500">{review.communication}/5</span>
                                    </div>
                                  </div>
                                )}
                                {review.qualityPriceRatio && (
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-700">{t("professionalReviewsSection.qualityPriceRatio")}</span>
                                    <div className="flex items-center gap-2">
                                      <StarRatingDisplay rating={review.qualityPriceRatio} size="sm" />
                                      <span className="text-xs text-gray-500">{review.qualityPriceRatio}/5</span>
                                    </div>
                                  </div>
                                )}
                                {review.clarity && (
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-700">{t("professionalReviewsSection.clarity")}</span>
                                    <div className="flex items-center gap-2">
                                      <StarRatingDisplay rating={review.clarity} size="sm" />
                                      <span className="text-xs text-gray-500">{review.clarity}/5</span>
                                    </div>
                                  </div>
                                )}
                                {review.reliability && (
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-700">{t("professionalReviewsSection.reliability")}</span>
                                    <div className="flex items-center gap-2">
                                      <StarRatingDisplay rating={review.reliability} size="sm" />
                                      <span className="text-xs text-gray-500">{review.reliability}/5</span>
                                    </div>
                                  </div>
                                )}
                                {review.payment && (
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-700">{t("professionalReviewsSection.payment")}</span>
                                    <div className="flex items-center gap-2">
                                      <StarRatingDisplay rating={review.payment} size="sm" />
                                      <span className="text-xs text-gray-500">{review.payment}/5</span>
                                    </div>
                                  </div>
                                )}
                                {review.availability && (
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-700">{t("professionalReviewsSection.availability")}</span>
                                    <div className="flex items-center gap-2">
                                      <StarRatingDisplay rating={review.availability} size="sm" />
                                      <span className="text-xs text-gray-500">{review.availability}/5</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                        <div className="flex flex-wrap gap-2 mt-3">
                          {review.tags.map((tag, index) => (
                            <Badge key={index} variant="outline" className="bg-gray-50">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div className="mt-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div className="flex flex-wrap items-center gap-4">
                          <VoteDisplayButton
                            reviewId={review.id}
                            votes={(originalReview as any)?.votes || []}
                            currentUserId={user?.id}
                            className=""
                          />
                          <button className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
                            <Share2 className="h-4 w-4" />
                            {t("professionalReviewsSection.share")}
                          </button>
                          <button className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
                            <Flag className="h-4 w-4" />
                            {t("professionalReviewsSection.report")}
                          </button>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                          <Button onClick={() => {
                            window.location.href = "/MessageAndVideoCall";
                            // navigate("/MessageAndVideoCall")
                          }} variant="outline" size="sm" className="text-blue-600 hover:text-blue-700 flex-1 sm:flex-none">
                            <MessageCircle className="h-4 w-4 mr-1" />
                            {t("professionalReviewsSection.messageClient")}
                          </Button>
                        </div>
                      </div>

                      

                      {/* Reply Button and Form */}
                      <div className="mt-3 flex items-center gap-4">
                        {/* See More Button - Show when there are nested reviews */}
                        {(review.review && Array.isArray(review.review) && review.review.length > 0 || review.response) && (
                          <button
                            onClick={() => toggleNestedReviews(review.id)}
                            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 hover:underline"
                          >
                            <MessageSquare className="h-4 w-4" />
                            {expandedNestedReviews.has(review.id) ? t("professionalReviewsSection.showLess") : t("professionalReviewsSection.seeMoreNested", { count: review.review?.length || 0 })}
                          </button>
                        )}

                        {/* Reply Button - Only show for clients who can reply */}
                        {canClientReply(review) && !review.response && (
                          <button
                            onClick={() => setReplyingToReview(review.id)}
                            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 hover:underline"
                          >
                            <MessageSquare className="h-4 w-4" />
                            {t("professionalReviewsSection.reply")}
                          </button>
                        )}
                      </div>

                      {/* Reply Form */}
                      {replyingToReview === review.id && (
                        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="mb-3">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t("professionalReviewsSection.yourReply")}
                            </label>
                            <textarea
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                              placeholder={t("professionalReviewsSection.replyPlaceholder")}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                              rows={3}
                              maxLength={500}
                            />
                            <div className="text-xs text-gray-500 mt-1">
                              {t("professionalReviewsSection.charactersCount", { count: replyText.length })}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleReplySubmit(review.id)}
                              disabled={!replyText.trim() || isSubmittingReply}
                              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                            >
                              <Send className="h-4 w-4" />
                              {isSubmittingReply ? t("professionalReviewsSection.submitting") : t("professionalReviewsSection.submitReply")}
                            </button>
                            <button
                              onClick={handleReplyCancel}
                              disabled={isSubmittingReply}
                              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                            >
                              {t("professionalReviewsSection.cancel")}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Nested Reviews Display */}
                      {expandedNestedReviews.has(review.id) && review.review && Array.isArray(review.review) && review.review.length > 0 && (
                        <div className="mt-4 pl-6 border-l-4 border-gray-200">
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-3">{t("professionalReviewsSection.relatedReviews", { count: review.review?.length || 0 })}</h4>
                            <div className="space-y-3">
                              {review.review.map((nestedReview: any, index: number) => (
                                <div key={index} className="p-3 bg-gray-50 rounded-lg">
                                  <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                                      <User className="h-4 w-4 text-gray-500" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1">
                                        <h5 className="font-medium text-gray-900 text-sm">
                                          {nestedReview.author?.firstName && nestedReview.author?.lastName
                                            ? `${nestedReview.author.firstName} ${nestedReview.author.lastName}`.trim()
                                            : nestedReview.author?.id || t("professionalReviewsSection.anonymous")}
                                        </h5>
                                        <div className="flex items-center gap-1">
                                          <StarRatingDisplay rating={nestedReview.rating || 0} size="sm" />
                                          <span className="text-xs text-gray-500">{nestedReview.rating || 0}/5</span>
                                        </div>
                                      </div>
                                      <p className="text-xs text-gray-500 mb-2">
                                        {new Date(nestedReview.createdAt || Date.now()).toLocaleDateString()}
                                      </p>
                                      {nestedReview.text && (
                                        <p className="text-gray-700 text-sm leading-relaxed">
                                          {nestedReview.text}
                                        </p>
                                      )}
                                      {nestedReview.reply && (
                                        <div className="mt-2 p-2 bg-blue-50 rounded border-l-2 border-blue-300">
                                          <p className="text-xs text-blue-600 font-medium mb-1">{t("professionalReviewsSection.response")}</p>
                                          <p className="text-gray-700 text-sm">{nestedReview.reply}</p>
                                          <ReplyVoteButton
                                            reviewId={nestedReview.id}
                                            hasReply={!!nestedReview.reply}
                                            votes={nestedReview.votes || []}
                                            currentUserId={user?.id}
                                          />
                                        </div>
                                      )}

                                      {/* Reply button for nested reviews */}
                                      {canClientReply(nestedReview) && !nestedReview.reply && (
                                        <div className="mt-3">
                                          <button
                                            onClick={() => setReplyingToReview(`${review.id}-${index}`)}
                                            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 hover:underline"
                                          >
                                            <MessageSquare className="h-4 w-4" />
                                            {t("professionalReviewsSection.reply")}
                                          </button>
                                        </div>
                                      )}

                                      {/* Reply form for nested reviews */}
                                      {replyingToReview === `${review.id}-${index}` && (
                                        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                          <div className="mb-3">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                              {t("professionalReviewsSection.yourReply")}
                                            </label>
                                            <textarea
                                              value={replyText}
                                              onChange={(e) => setReplyText(e.target.value)}
                                              placeholder={t("professionalReviewsSection.replyPlaceholder")}
                                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                              rows={3}
                                              maxLength={500}
                                            />
                                            <div className="text-xs text-gray-500 mt-1">
                                              {t("professionalReviewsSection.charactersCount", { count: replyText.length })}
                                            </div>
                                          </div>
                                          <div className="flex gap-2">
                                            <button
                                              onClick={() => handleReplySubmit(nestedReview.id)}
                                              disabled={!replyText.trim() || isSubmittingReply}
                                              className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                            >
                                              <MessageSquare className="h-3 w-3" />
                                              {isSubmittingReply ? t("professionalReviewsSection.submitting") : t("professionalReviewsSection.submitReply")}
                                            </button>
                                            <button
                                              onClick={handleReplyCancel}
                                              disabled={isSubmittingReply}
                                              className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                            >
                                              {t("professionalReviewsSection.cancel")}
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="pt-4">
                            {/* Reply Section */}
                            {review.response && (
                              <div className="mt-4 bg-blue-50 p-4 rounded-lg border border-blue-100">
                                <p className="text-sm font-medium text-blue-900">
                                  {user && Number(user.role?.id) === RoleEnum.client ? t("professionalReviewsSection.clientResponse") : t("professionalReviewsSection.officialResponse")}
                                </p>
                                <p className="mt-1 text-sm text-blue-800">
                                  {(() => {
                                    const reply = String(review.response || "");
                                    const isExpanded = expandedReviews.has(`${review.id}-reply`);
                                    const shouldTruncate = reply.length > 200;

                                    if (!shouldTruncate || isExpanded) {
                                      return reply;
                                    }

                                    return reply.substring(0, 200) + "...";
                                  })()}
                                </p>

                                {(() => {
                                  const reply = String(review.response || "");
                                  const isExpanded = expandedReviews.has(`${review.id}-reply`);
                                  const shouldTruncate = reply.length > 200;

                                  if (shouldTruncate) {
                                    return (
                                      <button
                                        onClick={() => toggleReviewExpansion(`${review.id}-reply`)}
                                        className="mt-2 text-sm text-blue-600 hover:text-blue-700 hover:underline"
                                      >
                                        {isExpanded ? t("professionalReviewsSection.showLess") : t("professionalReviewsSection.seeMore")}
                                      </button>
                                    );
                                  }
                                  return null;
                                })()}

                                <ReplyVoteButton
                                  reviewId={review.id}
                                  hasReply={!!review.response}
                                  votes={(originalReview as any)?.votes || []}
                                  currentUserId={user?.id}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </motion.div>
                    );
                  })}
                </AnimatePresence>
              )}
            </div>

            {/* Pagination */}
            {shouldShowPagination && (
              <div className="mt-8 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 px-4 sm:px-6 py-4 bg-gray-50 border-t">
                  <div className="text-sm text-gray-600">
                    {t("professionalReviewsSection.showingReviews", { 
                      start: ((currentPage - 1) * reviewsPerPage) + 1, 
                      end: Math.min(currentPage * reviewsPerPage, authorReviewsTotalCount || 0), 
                      total: authorReviewsTotalCount || 0 
                    })}
                  </div>

                <div className="flex items-center gap-2">
                  {/* Previous Button */}
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!hasPreviousPage}
                    className="flex items-center gap-1 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    {t("professionalReviewsSection.previous")}
                  </button>

                  {/* Page Numbers */}
                  <div className="flex items-center gap-1">
                    {/* Show first page */}
                    {currentPage > 3 && (
                      <>
                        <button
                          onClick={() => handlePageChange(1)}
                          className="px-3 py-2 text-sm border rounded-lg hover:bg-gray-50"
                        >
                          1
                        </button>
                        {currentPage > 4 && (
                          <span className="px-2 text-gray-400">...</span>
                        )}
                      </>
                    )}

                    {/* Show pages around current page */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      if (pageNum < 1 || pageNum > totalPages) return null;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-3 py-2 text-sm border rounded-lg ${
                            currentPage === pageNum
                              ? "bg-blue-600 text-white border-blue-600"
                              : "hover:bg-gray-50"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    {/* Show last page */}
                    {currentPage < totalPages - 2 && (
                      <>
                        {currentPage < totalPages - 3 && (
                          <span className="px-2 text-gray-400">...</span>
                        )}
                        <button
                          onClick={() => handlePageChange(totalPages)}
                          className="px-3 py-2 text-sm border rounded-lg hover:bg-gray-50"
                        >
                          {totalPages}
                        </button>
                      </>
                    )}
                  </div>

                  {/* Next Button */}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!hasNextPage}
                    className="flex items-center gap-1 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("professionalReviewsSection.next")}
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>

      <footer className="mt-auto bg-white border-t w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SharedFooter3 />
        </div>
      </footer>
    </div>
  );
};

export default ProfessionalReviewsSection;